import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Configurar el worker de PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const SecurePDFViewer = ({ pdfUrl }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);

  // 1. Cargar el documento PDF una sola vez al montar o cambiar la URL
  useEffect(() => {
    const loadPDF = async () => {
      try {
        setLoading(true);
        const loadingTask = pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        setPageNum(1);
        setLoading(false);
      } catch (error) {
        console.error("Error al cargar el PDF:", error);
        setLoading(false);
      }
    };

    loadPDF();
  }, [pdfUrl]);

  // 2. Renderizar con alta resolución basada en el ratio de píxeles de la pantalla
  const renderPage = useCallback(async (num) => {
    if (!pdfDoc) return;

    try {
      const page = await pdfDoc.getPage(num);
      
      const containerWidth = containerRef.current ? containerRef.current.clientWidth : 600;
      const unscaledViewport = page.getViewport({ scale: 1.0 });
      
      // Calcular escala base para ajustarse al ancho
      const baseScale = (containerWidth - 32) / unscaledViewport.width;
      const scale = baseScale > 0 ? baseScale : 1;

      // Obtener la densidad de píxeles del dispositivo (ej: 2 o 3 en teléfonos modernos)
      const outputScale = window.devicePixelRatio || 1;

      // Viewport escalado para alta definición
      const viewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Ajustar resolución interna del canvas multiplicada por el devicePixelRatio
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);

      // Forzar mediante CSS que el canvas conserve el tamaño visual correcto en la interfaz
      canvas.style.width = Math.floor(viewport.width) + 'px';
      canvas.style.height = Math.floor(viewport.height) + 'px';

      // Escalar el contexto para que PDF.js dibuje de forma nítida acorde a la alta densidad
      const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
        transform: transform,
      };

      await page.render(renderContext).promise;
    } catch (error) {
      console.error("Error al renderizar la página:", error);
    }
  }, [pdfDoc]);

  // Actualizar el renderizado cada vez que cambie la página actual o el documento
  useEffect(() => {
    if (pdfDoc) {
      renderPage(pageNum);
    }
  }, [pdfDoc, pageNum, renderPage]);

  // Manejadores de navegación
  const goToPrevPage = () => {
    if (pageNum > 1) setPageNum(pageNum - 1);
  };

  const goToNextPage = () => {
    if (pageNum < numPages) setPageNum(pageNum + 1);
  };



  return (
    <div 
      ref={containerRef} 
      style={{
        width: '100%',
        maxWidth: '1360px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        userSelect: 'none',
        fontFamily: 'sans-serif'
      }} 
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Barra de Controles de Paginación */}
      {!loading && numPages > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          padding: '10px 15px',
          backgroundColor: '#f1f5f9',
          borderRadius: '8px 8px 0 0',
          boxSizing: 'border-box',
          marginBottom: '10px'
        }}>
          <button 
            onClick={goToPrevPage} 
            disabled={pageNum <= 1}
            style={{
              padding: '8px 14px',
              backgroundColor: pageNum <= 1 ? '#cbd5e1' : '#0284c7',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: pageNum <= 1 ? 'not-allowed' : 'pointer',
              fontWeight: 'bold'
            }}
          >
            Anterior
          </button>
          
          <span style={{ fontSize: '14px', color: '#334155', fontWeight: '500' }}>
            Página <strong>{pageNum}</strong> de <strong>{numPages}</strong>
          </span>

          <button 
            onClick={goToNextPage} 
            disabled={pageNum >= numPages}
            style={{
              padding: '8px 14px',
              backgroundColor: pageNum >= numPages ? '#cbd5e1' : '#0284c7',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: pageNum >= numPages ? 'not-allowed' : 'pointer',
              fontWeight: 'bold'
            }}
          >
            Siguiente
          </button>
        </div>
      )}

      {/* Contenedor del Visor (Canvas) */}
      <div style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        overflow: 'hidden',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        borderRadius: '0 0 8px 8px',
        // backgroundColor: '#e2e8f0',
        minHeight: '400px'
      }}>
        {loading && <p style={{ padding: '40px', color: '#64748b' }}>Cargando documento...</p>}
        <canvas ref={canvasRef} style={{ maxWidth: '100%', height: 'auto', display: 'block' }} />
      </div>
    </div>
  );
};

export { SecurePDFViewer };